/*
**************************************************************************
Title:          Messages service
Created By:     Pankaj Arora
Created on:     05/02/2018
Description:    This service connects AI Chatbot to backend Framework
**************************************************************************
*/

import { Injectable } from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { Subject, Observable } from 'rxjs';
import { User } from '../users/user.model';
import { Message } from '../messages/message.model';
import { map, filter, scan, publishReplay, refCount } from 'rxjs/operators';

const initialMessages: Message[] = [];

type IMessagesOperation = (messages: Message[]) => Message[];

@Injectable()
export class MessageService {
    // a stream that publishes new messages only once
    newMessages: Subject<Message> = new Subject<Message>();

    // `messages` is a stream that emits an array of the most up to date messages
    messages: Observable<Message[]>;

    // `updates` receives _operations_ to be applied to our `messages`
    // it's a way we can perform changes on *all* messages (that are currently
    // stored in `messages`)
    updates: Subject<any> = new Subject<any>();

    // action streams
    create: Subject<Message> = new Subject<Message>();

    constructor() {

        this.messages = this.updates.pipe(
            // watch the updates and accumulate operations on the messages
            scan((messages: Message[], operation: IMessagesOperation) => {
                try {
                    return operation(messages);
                } catch (ex) {
                    return [];
                }
            },
                initialMessages)),
            // make sure we can share the most recent list of messages across anyone
            // who's interested in subscribing and cache the last known list of
            // messages
            publishReplay(1),
            refCount();

        // `create` takes a Message and then puts an operation (the inner function)
        // on the `updates` stream to add the Message to the list of messages.
        //
        // That is, for each item that gets added to `create` (by using `next`)
        // this stream emits a concat operation function.
        //
        // Next we subscribe `this.updates` to listen to this stream, which means
        // that it will receive each operation that is created
        //
        // Note that it would be perfectly acceptable to simply modify the
        // "addMessage" function below to simply add the inner operation function to
        // the update stream directly and get rid of this extra action stream
        // entirely. The pros are that it is potentially clearer. The cons are that
        // the stream is no longer composable.
        this.create.pipe(
            map((message: Message): IMessagesOperation =>
                (messages: Message[]) => messages.concat(message)))
            .subscribe(this.updates);

        this.newMessages
            .subscribe(this.create);

    }

    // an imperative function call to this action stream
    addMessage(message: Message): void {
        this.newMessages.next(message);
    }

    clearMessages(): void {
        this.updates.next(1);
    }

}

export const messagesServiceInjectables: Array<any> = [
    MessageService
];
